import React, { useMemo } from "react";
import { useTheme } from "styled-components";
import { AssetType, Flex, ItemType, Text, useBoolean, utils } from "@thenounproject/lingo-core";
import ColorPickerButton from "./ColorPickerButton";
import {
  AssetBackgroundColorOptions,
  NoteBackgroundColorOptions,
} from "../../components/NoteBackgroundColorOptions";
import { getSharedAttribute } from "./utils";
import Images from "@src/images";
import { ActionContext } from "../registry/types";

type Props = {
  context: ActionContext;
  editPreviewBackground: (background: string) => void;
  editCardBackground: (background: string | null) => void;
};

const { getColor } = utils;

const PRESET_LABELS: Record<string, string> = {
  default: "Default",
  clear: "None",
  light: "Light",
  dark: "Dark",
  checkered: "Checkered",
  noteInfoColor: "Info",
  noteWarningColor: "Warning",
  noteSuccessColor: "Success",
  noteNeutralColor: "Neutral",
};

export const ColorOptions: React.FC<Props> = ({
  context,
  editPreviewBackground,
  editCardBackground,
}) => {
  const {
    selection,
    location: { inGallery, inLibrary, inAssetDetail },
  } = context;
  const theme = useTheme();
  const previewOnly = Boolean(inGallery) || inLibrary;

  const [previewMenuVisible, showPreviewMenu, hidePreviewMenu] = useBoolean(false);
  const [cardMenuVisible, showCardMenu, hideCardMenu] = useBoolean(false);

  const assetItems = selection.assetItems?.length > 0 ? selection.assetItems : selection.assets;

  const selectionIsAllNotes = useMemo(
    () => selection.all.every(i => i.item?.type === ItemType.note),
    [selection.all]
  );

  const previewBackgroundEnabled = useMemo(
    () => assetItems.some(i => ![AssetType.color, AssetType.textStyle].includes(i.asset.type)),
    [assetItems]
  );

  const previewBackgroundPreset = useMemo(() => {
    return getSharedAttribute(assetItems, i => {
      return i.asset?.meta?.backgroundColor ?? "default";
    });
  }, [assetItems]);

  const cardBackgroundItems = selection.cardBackgroundCompatibleItems;

  const cardBackgroundPreset = useMemo(
    () =>
      getSharedAttribute(
        cardBackgroundItems,
        i => i.item?.displayProperties?.cardBackgroundColor ?? "default"
      ),
    [cardBackgroundItems]
  );

  const resolvePreviewColor = (preset: string | null | undefined): string | null => {
    if (!preset || preset === "default") return null;
    if (preset.startsWith("#")) return preset;
    const activeTheme = theme?.active;
    if (preset === "light")
      return activeTheme && theme.assetLightColor ? theme.assetLightColor : getColor("grayLighter");
    if (preset === "dark")
      return activeTheme && theme.assetDarkColor ? theme.assetDarkColor : getColor("grayDark");
    if (preset === "clear" || preset === "checkered") return null;

    return null;
  };

  const resolveCardColor = (preset: string | null | undefined): string | null => {
    if (!preset || preset === "default") return null;
    if (preset.startsWith("#")) return preset;

    const activeTheme = theme?.active;
    const colorMap: Record<string, string> = {
      noteInfoColor:
        activeTheme && theme.noteInfoColor ? theme.noteInfoColor : getColor("noteInfo"),
      noteWarningColor:
        activeTheme && theme.noteWarningColor ? theme.noteWarningColor : getColor("noteWarning"),
      noteSuccessColor:
        activeTheme && theme.noteSuccessColor ? theme.noteSuccessColor : getColor("noteSuccess"),
      noteNeutralColor:
        activeTheme && theme.noteNeutralColor ? theme.noteNeutralColor : getColor("noteNeutral"),
    };

    return colorMap[preset] || null;
  };

  const getDisplayLabel = (preset: string | null | undefined, itemCount: number): string => {
    if (!preset && itemCount > 1) return "Mixed";
    if (!preset || preset === "default") return "Default";
    if (preset.startsWith("#")) return preset;

    return PRESET_LABELS[preset] || preset;
  };

  const showPreviewBackground = previewBackgroundEnabled;
  const showCardBackground =
    !inAssetDetail && !previewOnly && (cardBackgroundItems.length > 0 || selectionIsAllNotes);

  if (!showPreviewBackground && !showCardBackground) return null;

  return (
    <Flex key="color-options" gap="4px">
      {showPreviewBackground && (
        <Flex flexDirection="column" alignItems="center" flex={1} width="100%">
          <ColorPickerButton
            popupSource="preview-background-color-menu"
            displayColor={resolvePreviewColor(previewBackgroundPreset)}
            displayLabel={getDisplayLabel(previewBackgroundPreset, selection.all.length)}
            iconId="content.color"
            menuVisible={previewMenuVisible}
            onShowMenu={showPreviewMenu}
            onHideMenu={hidePreviewMenu}
            swatchStyle={
              previewBackgroundPreset === "checkered"
                ? {
                    backgroundSize: "32px",
                    backgroundImage: `url(${Images.App.transparentColorBackground})`,
                  }
                : undefined
            }>
            <AssetBackgroundColorOptions
              hPos="center"
              optionsOnly
              editAssetBackground={editPreviewBackground}
              selectedPreset={previewBackgroundPreset}
            />
          </ColorPickerButton>
          <Text mt="xs" font="ui.extraTiny" color="grayDark">
            Thumbnail
          </Text>
        </Flex>
      )}

      {showCardBackground && (
        <Flex flexDirection="column" alignItems="center" flex={1} width="100%">
          <ColorPickerButton
            popupSource="card-background-color-menu"
            displayColor={resolveCardColor(cardBackgroundPreset)}
            displayLabel={getDisplayLabel(cardBackgroundPreset, cardBackgroundItems.length)}
            iconId="content.color"
            menuVisible={cardMenuVisible}
            onShowMenu={showCardMenu}
            onHideMenu={hideCardMenu}>
            <NoteBackgroundColorOptions
              hPos="center"
              nested={false}
              editItemBackground={editCardBackground}
              selectedPreset={cardBackgroundPreset}
            />
          </ColorPickerButton>
          <Text mt="xs" font="ui.extraTiny" color="grayDark">
            Card
          </Text>
        </Flex>
      )}
    </Flex>
  );
};
